/**
 * The PLAN task's spec, split into the part that is the same on every job and
 * the part that is this job.
 *
 * `POST /api/briefs/:id/plan` stores the whole planning prompt
 * (`buildPlanningPrompt`, `packages/core/src/planning.ts`) as the synthetic PLAN
 * task's spec, because the spec is what the planning agent was actually told and
 * the console shows a person exactly that. Read as a page, though, it is a wall:
 * the role preamble, the floor rules and a verbatim JSON schema dump, with the
 * three lines a person opened the page for buried at the top. This finds the
 * seam so the page can fold the scaffolding away without hiding it.
 *
 * The headings below are mirrored from `buildPlanningPrompt` rather than
 * imported, the same arrangement `PLAN_KEY` in `lib/briefs.ts` uses and for the
 * same reason: importing a *value* from `@dyspatch/core` pulls `node:crypto`
 * into the browser bundle. A test in `packages/hub/test/plan-spec.test.ts` feeds
 * real `buildPlanningPrompt` output through this, so a heading moving in core
 * fails the build rather than quietly turning the page back into a wall.
 */

/**
 * Every heading `buildPlanningPrompt` emits, in the order it emits them.
 * `# Capacity` is the one that is optional: the prompt drops the capacity steer
 * when nobody measured the fleet.
 */
export const PLAN_PROMPT_HEADINGS: readonly string[] = [
  '# The brief',
  '# Capacity',
  '# How to work',
  '# The floor',
  '# What each task must be',
  '# Your final message',
];

/** Where the brief-specific half starts. */
const BRIEF_HEADING = '# The brief';
/** Where the fixed scaffolding resumes, and the brief-specific half ends. */
const WORK_HEADING = '# How to work';

export interface PlanPromptParts {
  /** The role preamble plus everything from `# How to work` down, schema included. */
  systemPrompt: string;
  /** `# The brief` and the `# Capacity` steer that follows it: this job's own words. */
  userPrompt: string;
}

/**
 * The offset of a heading that sits alone on its own line, or -1.
 *
 * Anchored rather than found with `indexOf`, because "# The brief" is a phrase a
 * person can perfectly well write inside a goal paragraph, and a split taken at
 * that offset would put half the goal in the wrong disclosure.
 */
function headingIndex(spec: string, heading: string): number {
  const escaped = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return spec.search(new RegExp(`^${escaped}[ \\t]*$`, 'm'));
}

/**
 * The two halves of a planning prompt, or `null` when this spec is not one.
 *
 * `null` is the honest answer for any spec a person rewrote through the "Change"
 * verb (`api.setTaskSpec`): once the headings are gone it is just instructions,
 * and the page falls back to drawing it whole. Nothing is dropped either way -
 * the two parts hold every non-whitespace character the original had.
 */
export function splitPlanPrompt(spec: string): PlanPromptParts | null {
  const briefAt = headingIndex(spec, BRIEF_HEADING);
  const workAt = headingIndex(spec, WORK_HEADING);
  if (briefAt < 0 || workAt < 0 || briefAt >= workAt) return null;

  const preamble = spec.slice(0, briefAt).trimEnd();
  const userPrompt = spec.slice(briefAt, workAt).trimEnd();
  const scaffolding = spec.slice(workAt).trimEnd();

  return {
    systemPrompt: [preamble, scaffolding].filter((part) => part.length > 0).join('\n\n'),
    userPrompt,
  };
}
